import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef
} from "react";
import { useTranslation } from "react-i18next";
import Text from "@/components/ui/text";
import { PeopleView } from "@/entities/people/types";
import { usePeopleTable } from "@/services/hooks/entities/person/usePeopleTable";
import PeopleTable from "@/components/layouts/People/PeopleTable";
import {
  EmptyView,
  ViewDataConfiguration,
  ViewType
} from "@/entities/views/types";
import { ColumnData } from "@/entities/dataTables/types";
import {
  saveConfigToTempView,
  getTempView
} from "@/entities/dataTables/helpers";
import { getFullName } from "@/entities/people/helpers";
import AddPeopleModal from "@/components/layouts/AddPeopleModal/AddPeopleModal";
import {
  useContract,
  useUpdateContract,
  ContractUpdatePayload
} from "@/pages/contracts/hooks";
import { useCompanies } from "@/pages/companies/hooks";
import { useQueryClient } from "@tanstack/react-query";
import EmptyState from "@/components/layouts/PeopleTab/components/EmptyState";
import PeopleCardsGrid from "@/components/layouts/PeopleTab/components/PeopleCardsGrid";
import NewPersonButton from "@/components/layouts/PeopleTab/components/NewPersonButton";
import { isEmpty as lodashIsEmpty, isEqual } from "lodash";
import { usePeopleSelection } from "@/services/hooks/entities/person/usePeopleSelection";
import PeopleBulkActionPopover from "@/components/layouts/PeopleTab/components/PeopleBulkActionPopover";
import ConfirmationModal from "@/components/layouts/ConfirmationModal";
import toast from "react-hot-toast";
import ToolbarActions from "@/components/controls/ToolbarActions/ToolbarActions";
import Tabs from "@/components/controls/Tabs";
import { Cards, Table, Account, Partners } from "@dreamhub/designsystem/icons";
import { CONTRACT_PEOPLE_TABLE_ID } from "@/components/layouts/PeopleTab/constants";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { getEnumTKey } from "@/utils/strings";
import PartnerContactsTable from "@/components/layouts/PeopleTab/components/PartnerContactsTable";
import { useEnablePartnerships } from "@/pages/partners/hooks";

export { CONTRACT_PEOPLE_TABLE_ID };

const TRANSLATION_PREFIX =
  "detailedView.peopleTab.bulkActions.removeConfirmationModal";

enum PeopleSource {
  COMPANY = "company",
  PARTNER = "partner"
}

interface Props {
  contractId: string;
  companyId: string;
}

const ContractPeopleTab: React.FC<Props> = ({ contractId, companyId }) => {
  const { t } = useTranslation();
  const entityName = t(
    getEnumTKey("entities", "names", EntityType, EntityType.CONTRACT)
  ).toLowerCase();
  const queryClient = useQueryClient();
  const { data: contract } = useContract(contractId);
  const updateContract = useUpdateContract(contractId);
  // Mutation objects get a new identity on every state change — keep it in a
  // ref so the update callbacks stay referentially stable.
  const updateContractRef = useRef(updateContract);
  useEffect(() => {
    updateContractRef.current = updateContract;
  }, [updateContract]);
  const {
    entities: [companyData]
  } = useCompanies([companyId]);

  const hiddenColumns = ["companyId", "companyName", "id"];

  const [tabValue, setTabValue] = useState(PeopleView.TABLE.toString());
  const enablePartnerships = useEnablePartnerships();
  const [peopleSource, setPeopleSource] = useState<PeopleSource>(
    PeopleSource.COMPANY
  );
  // Partner contacts only exist for accounts that have partnerships set up, so
  // when it is off the Company/Partner toggle is hidden and the view is pinned
  // to company people.
  const isPartnerSource =
    enablePartnerships && peopleSource === PeopleSource.PARTNER;
  const [isAddPeopleModalOpen, setIsAddPeopleModalOpen] = useState(false);
  const [isRemoveConfirmationOpen, setIsRemoveConfirmationOpen] =
    useState(false);
  const [selectedView] = useState<ViewDataConfiguration>(() => ({
    ...EmptyView(ViewType.PEOPLE_TABLE),
    id: CONTRACT_PEOPLE_TABLE_ID,
    isOpen: true
  }));

  const [columnsState, setColumnsState] = useState<ColumnData[]>(() => {
    const tempView = getTempView(selectedView);
    return tempView?.config?.columns ?? [];
  });

  const handleColumnsChange = useCallback(
    (updatedColumns?: ColumnData[]) => {
      if (updatedColumns) {
        setColumnsState(updatedColumns);
        saveConfigToTempView(selectedView, {
          columns: updatedColumns
        });
      }
    },
    [selectedView]
  );

  useEffect(() => {
    const existingView = getTempView(selectedView);
    if (!existingView) {
      saveConfigToTempView(selectedView, {
        filters: {},
        columns: []
      });
    }
  }, [selectedView]);

  const allContractPeople = useMemo(() => {
    const manual = contract?.manuallyAddedPeople ?? [];
    const automatic = contract?.automaticallyAddedPeople ?? [];
    return [...new Set([...manual, ...automatic])];
  }, [contract?.manuallyAddedPeople, contract?.automaticallyAddedPeople]);

  const partnerContactIds = useMemo(() => {
    const manual = contract?.manuallyAddedPartnerContacts ?? [];
    const automatic = contract?.automaticallyAddedPartnerContacts ?? [];
    return [...new Set([...manual, ...automatic])];
  }, [
    contract?.manuallyAddedPartnerContacts,
    contract?.automaticallyAddedPartnerContacts
  ]);

  const initialFilters = useMemo(() => {
    return allContractPeople.length > 0
      ? { id: { in: allContractPeople } }
      : {};
  }, [allContractPeople]);

  const {
    people,
    reportsToDataMap,
    sorting,
    onSortingChange,
    isLoading: peopleLoading,
    isFetchingNextPage,
    hasNextPage,
    searchTerm,
    setSearchTerm,
    filters,
    setFilters,
    peopleFilterOptions,
    peopleSortByOptions,
    handleLoadMore,
    resetPage,
    isError,
    refetch
  } = usePeopleTable({
    hiddenColumns,
    initialFilters,
    enabled: !!contract && allContractPeople.length > 0 && !isPartnerSource
  });

  const isLoading = !contract || peopleLoading;

  const {
    selectedPeople,
    selectAll,
    selectedPeopleData,
    handlePersonSelect,
    handleSelectAll,
    clearSelection
  } = usePeopleSelection(people);

  const getReportsToPersonName = (personId: string | null | undefined) => {
    if (lodashIsEmpty(personId)) {
      return "";
    }
    const person = reportsToDataMap[personId as string];
    if (!person) return t("notFound");
    return getFullName(person);
  };

  const handleAddPeopleToContract = useCallback(
    (newPeopleIds: string[]): Promise<void> => {
      return new Promise((resolve, reject) => {
        updateContractRef.current.mutate(
          {
            pathParams: { contractId },
            data: {
              manuallyAddedPeople: [
                ...(contract?.manuallyAddedPeople || []),
                ...newPeopleIds
              ]
            } as ContractUpdatePayload
          },
          {
            onSuccess: () => {
              queryClient.invalidateQueries({
                queryKey: ["contracts/id", { pathParams: { contractId } }]
              });
              resolve();
            },
            onError: (error) => {
              toast.error(t("contracts.detailedView.updateError"));
              reject(error);
            }
          }
        );
      });
    },
    [contract, contractId, queryClient, t]
  );

  const handleAddPartnerContactsToContract = useCallback(
    (newContactIds: string[]): Promise<void> => {
      return new Promise((resolve, reject) => {
        updateContractRef.current.mutate(
          {
            pathParams: { contractId },
            data: {
              manuallyAddedPartnerContacts: [
                ...(contract?.manuallyAddedPartnerContacts || []),
                ...newContactIds
              ]
            } as ContractUpdatePayload
          },
          {
            onSuccess: () => {
              queryClient.invalidateQueries({
                queryKey: ["contracts/id", { pathParams: { contractId } }]
              });
              resolve();
            },
            onError: (error) => {
              toast.error(t("contracts.detailedView.updateError"));
              reject(error);
            }
          }
        );
      });
    },
    [contract, contractId, queryClient, t]
  );

  const handleSelectPartner = useCallback(
    (selectedPartnerId: string): Promise<void> => {
      return new Promise((resolve, reject) => {
        updateContractRef.current.mutate(
          {
            pathParams: { contractId },
            data: {
              partnerId: selectedPartnerId
            }
          },
          {
            onSuccess: () => {
              queryClient.invalidateQueries({
                queryKey: ["contracts/id", { pathParams: { contractId } }]
              });
              resolve();
            },
            onError: (error) => {
              toast.error(t("contracts.detailedView.updateError"));
              reject(error);
            }
          }
        );
      });
    },
    [contractId, queryClient, t]
  );

  const handleRemoveFromContract = useCallback(() => {
    setIsRemoveConfirmationOpen(true);
  }, []);

  const handleConfirmRemoveFromContract = useCallback(async () => {
    const selectedPeopleIds = selectedPeopleData.map((person) => person.id);
    const newPeopleArray =
      contract?.manuallyAddedPeople?.filter(
        (personId) => !selectedPeopleIds.includes(personId)
      ) || [];
    const removablePeople = selectedPeopleIds.filter((id) =>
      contract?.manuallyAddedPeople?.includes(id)
    );
    updateContractRef.current.mutate(
      {
        pathParams: {
          contractId
        },
        data: {
          manuallyAddedPeople: newPeopleArray
        } as ContractUpdatePayload
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ["contracts/id", { pathParams: { contractId } }]
          });
          setTimeout(() => {
            resetPage?.();
          }, 100);
          setIsRemoveConfirmationOpen(false);
          clearSelection();
          toast.success(
            t(`${TRANSLATION_PREFIX}.success`, {
              count: removablePeople.length,
              entityName
            })
          );
        },
        onError: () => {
          toast.error(t("contracts.detailedView.updateError"));
        }
      }
    );
  }, [
    selectedPeopleData,
    clearSelection,
    contract?.manuallyAddedPeople,
    contractId,
    queryClient,
    resetPage,
    entityName,
    t
  ]);

  const handleCloseBulkActions = useCallback(() => {
    clearSelection();
  }, [clearSelection]);

  const sourceOptions = useMemo(
    () => ({
      [PeopleSource.COMPANY]: (
        <div className="flex gap-2 items-center [&>svg]:w-5 [&>svg]:h-5">
          <Account />
          <Text as="span">{t("detailedView.peopleTab.company")}</Text>
        </div>
      ),
      [PeopleSource.PARTNER]: (
        <div className="flex gap-2 items-center [&>svg]:w-5 [&>svg]:h-5">
          <Partners />
          <Text as="span">{t("detailedView.peopleTab.partner")}</Text>
        </div>
      )
    }),
    [t]
  );

  const showAsOptions = useMemo(
    () => [
      { value: PeopleView.TABLE.toString(), label: <Table /> },
      { value: PeopleView.CARDS.toString(), label: <Cards /> }
    ],
    []
  );

  const handleSourceChange = useCallback(
    (value: string) => {
      const nextSource = value as PeopleSource;
      if (nextSource !== peopleSource) {
        clearSelection();
      }
      setPeopleSource(nextSource);
    },
    [peopleSource, clearSelection]
  );

  const renderContent = () => {
    if (!contract) {
      return null;
    }

    return (
      <div className="flex flex-col w-full h-full overflow-hidden">
        <div>
          <ToolbarActions
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            filters={filters}
            setFilters={setFilters}
            filterOptions={peopleFilterOptions}
            sorting={sorting}
            onSortingChange={onSortingChange}
            sortByOptions={peopleSortByOptions}
            defaultSortByValue="fullName"
            showTableColumns={false}
            showDisplayButton={!isPartnerSource}
            showSearch={!isPartnerSource}
            showFilters={!isPartnerSource}
            showAsValue={tabValue}
            onShowAsChange={setTabValue}
            showAsOptions={showAsOptions}
            onColumnsChange={handleColumnsChange}
            tableId={CONTRACT_PEOPLE_TABLE_ID}
            className="p-4 border-none"
            baseFilters={initialFilters}
            leftActions={
              enablePartnerships ? (
                <Tabs
                  options={sourceOptions}
                  value={peopleSource}
                  valueSelected={handleSourceChange}
                  containerClassName="bg-surface-field-default border-[0.5px]"
                  className="px-2 py-1"
                />
              ) : undefined
            }
            rightActions={
              <NewPersonButton
                onClick={() => setIsAddPeopleModalOpen(true)}
                isDealMode={true}
                text={
                  isPartnerSource
                    ? t("detailedView.peopleTab.addPartnerContact")
                    : undefined
                }
              />
            }
          />
        </div>
        <div className="flex-1 overflow-hidden">
          {isPartnerSource ? (
            <PartnerContactsTable
              partnerContactIds={partnerContactIds}
              entityType={EntityType.CONTRACT}
              onAddPartnerContact={() => setIsAddPeopleModalOpen(true)}
              className="h-[66vh]"
            />
          ) : allContractPeople.length === 0 ? (
            <EmptyState
              onAddPeople={() => setIsAddPeopleModalOpen(true)}
              entityType={EntityType.CONTRACT}
            />
          ) : tabValue === PeopleView.TABLE.toString() ? (
            <PeopleTable
              people={people}
              sorting={sorting}
              onSortingChange={onSortingChange}
              isLoading={isLoading}
              isFetchingNextPage={isFetchingNextPage}
              hasNextPage={hasNextPage}
              onLoadMore={handleLoadMore}
              hiddenColumns={hiddenColumns}
              tableId={CONTRACT_PEOPLE_TABLE_ID}
              selectedPeople={selectedPeople}
              selectAll={selectAll}
              onPersonSelect={handlePersonSelect}
              onSelectAll={handleSelectAll}
              selectedView={selectedView}
              columnsState={columnsState}
              className="h-[66vh]"
              onColumnsChange={handleColumnsChange}
              hasActiveFilters={!isEqual(filters, initialFilters)}
              onClearFilters={() => setFilters(initialFilters)}
              onClearSearch={() => setSearchTerm("")}
              isError={isError}
              onRetry={refetch}
            />
          ) : (
            <PeopleCardsGrid
              people={people}
              getReportsToPersonName={getReportsToPersonName}
            />
          )}
        </div>
      </div>
    );
  };

  const getRemovalTitle = () => {
    const selectedCount = selectedPeopleData.length;
    const selectedPeopleIds = selectedPeopleData.map((person) => person.id);
    const removablePeople = selectedPeopleIds.filter((id) =>
      contract?.manuallyAddedPeople?.includes(id)
    );
    const nonRemovablePeople = selectedPeopleIds.filter(
      (id) => !contract?.manuallyAddedPeople?.includes(id)
    );

    if (nonRemovablePeople.length > 0 && removablePeople.length > 0) {
      return t(`${TRANSLATION_PREFIX}.partialTitle`);
    }

    if (selectedCount === 1) {
      const personName = getFullName(selectedPeopleData[0]);
      return t(`${TRANSLATION_PREFIX}.singleTitle`, {
        personName,
        entityName
      });
    } else {
      return t(`${TRANSLATION_PREFIX}.multiTitle`, { entityName });
    }
  };

  const getRemovalConfirmationText = () => {
    const companyName = companyData?.name || t("entities.names.company");
    const selectedCount = selectedPeopleData.length;
    const selectedPeopleIds = selectedPeopleData.map((person) => person.id);
    const removablePeople = selectedPeopleIds.filter((id) =>
      contract?.manuallyAddedPeople?.includes(id)
    );
    const nonRemovablePeople = selectedPeopleIds.filter(
      (id) => !contract?.manuallyAddedPeople?.includes(id)
    );

    if (nonRemovablePeople.length > 0 && removablePeople.length > 0) {
      return t(`${TRANSLATION_PREFIX}.partialText`, {
        removableCount: removablePeople.length,
        selectedCount,
        companyName,
        entityName
      });
    }

    if (selectedCount === 1) {
      const personName = getFullName(selectedPeopleData[0]);
      return t(`${TRANSLATION_PREFIX}.singleText`, {
        personName,
        companyName,
        entityName
      });
    } else {
      return t(`${TRANSLATION_PREFIX}.multiText`, {
        selectedCount,
        companyName,
        entityName
      });
    }
  };

  return (
    <>
      {renderContent()}
      {selectedPeople.size > 0 && (
        <PeopleBulkActionPopover
          selectedPeople={selectedPeopleData}
          onRemoveFromDeal={handleRemoveFromContract}
          onClose={handleCloseBulkActions}
          manuallyAddedPeople={contract?.manuallyAddedPeople || undefined}
          automaticallyAddedPeople={
            contract?.automaticallyAddedPeople || undefined
          }
          entityType={EntityType.CONTRACT}
        />
      )}
      {isAddPeopleModalOpen && (
        <AddPeopleModal
          isOpen={true}
          setIsOpen={setIsAddPeopleModalOpen}
          entityId={contractId}
          companyId={companyId}
          resetPage={resetPage}
          allDealPeople={allContractPeople}
          onAddPeople={handleAddPeopleToContract}
          entityType={EntityType.CONTRACT}
          mode={isPartnerSource ? "partnerContacts" : "people"}
          partnerId={contract?.partnerId}
          onSelectPartner={handleSelectPartner}
          addedPartnerContacts={partnerContactIds}
          onAddPartnerContacts={handleAddPartnerContactsToContract}
        />
      )}
      <ConfirmationModal
        isOpen={isRemoveConfirmationOpen}
        setIsOpen={setIsRemoveConfirmationOpen}
        title={getRemovalTitle()}
        confirmationText={getRemovalConfirmationText()}
        handleSubmit={handleConfirmRemoveFromContract}
        submitButtonText={t("remove")}
        cancelButtonText={t("cancel")}
        isPrimaryButton={false}
      />
    </>
  );
};

export default ContractPeopleTab;
